import type { EstimatedDuration } from '@axiom/const';
import { PositionsConst, selfServiceCountryCodes } from '@axiom/const';
import { TaxonomyUtil } from '@axiom/ui';
import type {
  Account,
  DSSuggestedIntake,
  ProjectNeedsWithLegalSkills,
  Taxonomy,
} from '@axiom/validation';

import { EngagementUtil } from '../../utils/engagement-util';

const applyIntakeSuggestion = <T extends Partial<ProjectNeedsWithLegalSkills>>({
  response,
  currentValues,
  skillGroups,
  legalSkills,
}: {
  response: DSSuggestedIntake;
  currentValues: T;
  skillGroups: Taxonomy['skillGroups'];
  legalSkills: Taxonomy['legalSkills'];
}): {
  mergedValues: T;
  updatedSkillGroups: ReturnType<
    typeof TaxonomyUtil.getFocusAreaSkillGroups
  > | null;
  shouldExpandAccordion: boolean;
} => {
  const mergedValues = { ...currentValues };

  // Only overwrite form values where AI returned a non-null value,
  // preserving existing Digital Intake Details for fields AI couldn't determine
  for (const [key, value] of Object.entries(response)) {
    if (value != null) {
      (mergedValues as Record<string, unknown>)[key] = value;
    }
  }

  if (response.worksite !== PositionsConst.Worksites.Remote) {
    delete (mergedValues as Record<string, unknown>).companyLocation;
  }

  if (mergedValues.startDate && mergedValues.estimatedDuration) {
    mergedValues.endDate = EngagementUtil.calculateEndDateFromEstimatedDuration(
      mergedValues.startDate,
      mergedValues.estimatedDuration as EstimatedDuration
    );
  }

  // Map legalSkills to legalSkillsAnswers based on skill groups
  let updatedSkillGroups: ReturnType<
    typeof TaxonomyUtil.getFocusAreaSkillGroups
  > | null = null;

  if (response.legalSkills && response.primaryFocusArea) {
    updatedSkillGroups = TaxonomyUtil.getFocusAreaSkillGroups(
      response.primaryFocusArea,
      skillGroups
    );

    mergedValues.legalSkillsAnswers = updatedSkillGroups.map(({ key }) => {
      const legalSkillOptions = TaxonomyUtil.getLegalSkillOptions(
        legalSkills,
        key
      );
      return legalSkillOptions
        .filter(({ value }) => (response.legalSkills ?? []).includes(value))
        .map(({ value }) => value);
    });
  }

  const shouldExpandAccordion = !!(
    response.generalSkills?.length ||
    response.legalTechSkills?.length ||
    response.languageId ||
    response.levelOfExperience ||
    response.industry
  );

  return { mergedValues, updatedSkillGroups, shouldExpandAccordion };
};

const defaultCountryCode = ({
  account,
}: {
  account: Pick<Account, 'hqAddressCountry'>;
}) =>
  account.hqAddressCountry &&
  account.hqAddressCountry in selfServiceCountryCodes
    ? (account.hqAddressCountry as keyof typeof selfServiceCountryCodes)
    : 'US';

export const DirectEngagementsFormIntakeUtil = {
  applyIntakeSuggestion,
  defaultCountryCode,
};
